import { motion } from 'framer-motion';
import type { Quest, QuestStatus } from '@/services/quests';

interface QuestCardProps {
  quest: Quest;
  onStart: () => void;
  onClaim: () => void;
  isLoading?: boolean;
}

const QUEST_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  daily: { label: 'Ежедневный', color: 'text-blue-400' },
  weekly: { label: 'Недельный', color: 'text-purple-400' },
  story: { label: 'Сюжетный', color: 'text-amber-400' },
  special: { label: 'Особый', color: 'text-pink-400' },
};

const REQUIREMENT_EMOJIS: Record<string, string> = {
  meditation: '🧘',
  breathing: '💨',
  reflection: '📝',
  any: '✨',
  checkin: '📍',
  social: '👥',
};

export function QuestCard({ quest, onStart, onClaim, isLoading }: QuestCardProps) {
  const status = quest.userProgress?.status || 'not_started';
  const progress = quest.userProgress?.currentProgress || 0;
  const target = quest.requirements.count;
  const progressPercent = Math.min((progress / target) * 100, 100);
  
  const typeInfo = QUEST_TYPE_LABELS[quest.questType] || { label: quest.questType, color: 'text-primary-400' };
  const requirementEmoji = REQUIREMENT_EMOJIS[quest.requirements.type] || '📋';
  
  const isCompleted = status === 'completed';
  const isClaimed = status === 'claimed';
  const isInProgress = status === 'in_progress';
  const isExpired = status === 'expired';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-4 ${
        isClaimed 
          ? 'bg-primary-900/30 opacity-60' 
          : isExpired
            ? 'bg-red-900/20 opacity-50'
            : 'bg-primary-800/50'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-700/50 flex items-center justify-center">
            <span className="text-xl">{requirementEmoji}</span>
          </div>
          <div>
            <h3 className="font-medium text-white">{quest.title}</h3>
            <p className={`text-xs ${typeInfo.color}`}>{typeInfo.label}</p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-sm font-medium text-primary-300">+{quest.xpReward} XP</p>
          {quest.achievementId && (
            <p className="text-xs text-amber-400">🏆 Достижение</p>
          )}
        </div>
      </div>
      
      {/* Description */}
      {quest.description && (
        <p className="text-sm text-primary-400 mb-3">{quest.description}</p>
      )}
      
      {/* Progress */}
      {(isInProgress || isCompleted) && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-primary-500 mb-1">
            <span>{quest.requirements.description || `${progress}/${target}`}</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-2 bg-primary-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full rounded-full ${
                isCompleted ? 'bg-green-500' : 'bg-primary-400'
              }`}
            />
          </div>
        </div>
      )}
      
      {/* Action Button */}
      {!isClaimed && !isExpired && (
        <QuestActionButton
          status={status}
          onStart={onStart}
          onClaim={onClaim}
          isLoading={isLoading}
        />
      )}
      
      {/* Status badges */}
      {isClaimed && (
        <div className="flex items-center justify-center gap-2 py-2 text-green-400 text-sm">
          <span>✓</span>
          <span>Награда получена</span>
        </div>
      )}
      
      {isExpired && (
        <div className="flex items-center justify-center gap-2 py-2 text-red-400 text-sm">
          <span>⏱</span>
          <span>Время истекло</span>
        </div>
      )}
    </motion.div>
  );
}

interface QuestActionButtonProps {
  status: QuestStatus;
  onStart: () => void;
  onClaim: () => void;
  isLoading?: boolean;
}

function QuestActionButton({ status, onStart, onClaim, isLoading }: QuestActionButtonProps) {
  if (status === 'not_started') {
    return (
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onStart}
        disabled={isLoading}
        className="w-full py-2 rounded-lg bg-primary-700 text-primary-200 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
      >
        {isLoading ? 'Начинаем...' : 'Начать квест'}
      </motion.button>
    );
  }
  
  if (status === 'completed') {
    return (
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onClaim}
        disabled={isLoading}
        className="w-full py-2 rounded-lg bg-gradient-to-r from-green-600 to-green-500 text-white text-sm font-medium hover:from-green-500 hover:to-green-400 transition-colors disabled:opacity-50"
      >
        {isLoading ? 'Получаем...' : '🎁 Забрать награду'}
      </motion.button>
    );
  }
  
  if (status === 'in_progress') {
    return (
      <div className="w-full py-2 text-center text-primary-500 text-sm">
        В процессе выполнения...
      </div>
    );
  }
  
  return null;
}
